import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";
import {
  costBoolean,
  costDate,
  costNullableNumber,
  costText,
  oneOf,
  toRemoteFlag,
} from "@/lib/api/cost-fields";
import type {
  ScheduledReport,
  ScheduledReportFormat,
  ScheduledReportFrequency,
  ScheduledReportRun,
  ScheduledReportRunStatus,
} from "@/types/fleet";
import { REPORT_TYPES } from "@/lib/reports/build-report";

/**
 * Scheduled report emails. Upstream (`ClientScheduledReport.php`, `ClientScheduledReportRun.php`)
 * stores the schedules, runs the worker and sends the mail; the rendering comes back to this app
 * through `/api/internal/scheduled-reports/render`. Wire shape: `docs/SCHEDULED_REPORTS_SERVER.md`.
 */

export const FREQUENCIES = ["daily", "weekly", "monthly"] as const satisfies readonly ScheduledReportFrequency[];
export const FORMATS = ["pdf", "xlsx"] as const satisfies readonly ScheduledReportFormat[];
export const LANGUAGES = ["en", "sr", "bs", "hr"] as const;
const RUN_STATUSES = ["ok", "failed", "retrying"] as const satisfies readonly ScheduledReportRunStatus[];

export const MAX_RECIPIENTS = 10;
export const DEFAULT_TIME_ZONE = "Europe/Sarajevo";

/** Upstream's NULL for "no filter" becomes the same `"all"` the Reports page comboboxes use. */
const ALL = "all";

type RemoteList = string[] | number[] | string | null | undefined;

interface RemoteScheduledReport {
  id: string;
  name?: string;
  report_type?: string;
  group_id?: string | null;
  vehicle_id?: string | null;
  frequency?: string;
  weekdays?: RemoteList;
  month_day?: string | number | null;
  send_time?: string;
  timezone?: string;
  language?: string;
  formats?: RemoteList;
  recipients?: RemoteList;
  is_active?: string;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_status?: string | null;
  last_error?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface RemoteScheduledReportRun {
  id: string;
  schedule_id?: string;
  scheduled_for?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  status?: string;
  attempt?: string | number;
  row_count?: string | number | null;
  recipients?: RemoteList;
  error_code?: string | null;
  error_message?: string | null;
  trigger?: string;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

/** JSON arrays are the documented shape; a MySQL SET's comma string is accepted too. */
function parseList(value: RemoteList): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (typeof value === "string") return value.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
}

function filterId(value: string | null | undefined): string {
  const id = costText(value);
  return id === "" || id === "0" ? ALL : id;
}

/** `HH:MM:SS` from a MySQL TIME column is trimmed to the `HH:MM` the form edits. */
function sendTime(value: string | undefined): string {
  const match = /^(\d{2}):(\d{2})/.exec(costText(value));
  return match ? `${match[1]}:${match[2]}` : "07:00";
}

/** `queued`/`running` are upstream-internal states of a run still in flight; they read as retrying. */
function runStatus(value: string | null | undefined): ScheduledReportRunStatus {
  const status = costText(value);
  if (status === "queued" || status === "running") return "retrying";
  return oneOf(status, RUN_STATUSES, "failed");
}

function mapScheduledReport(remote: RemoteScheduledReport): ScheduledReport {
  const weekdays = parseList(remote.weekdays)
    .map(Number)
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7)
    .sort((a, b) => a - b);
  const lastStatus = costText(remote.last_status);

  return {
    id: remote.id,
    name: costText(remote.name),
    reportType: oneOf(remote.report_type, REPORT_TYPES, "trips"),
    groupId: filterId(remote.group_id),
    vehicleId: filterId(remote.vehicle_id),
    frequency: oneOf(remote.frequency, FREQUENCIES, "daily"),
    weekdays,
    monthDay: costNullableNumber(remote.month_day ?? null),
    sendTime: sendTime(remote.send_time),
    timeZone: costText(remote.timezone) || DEFAULT_TIME_ZONE,
    language: oneOf(remote.language, LANGUAGES, "sr"),
    formats: parseList(remote.formats).filter((item): item is ScheduledReportFormat =>
      (FORMATS as readonly string[]).includes(item),
    ),
    recipients: parseList(remote.recipients),
    isActive: costBoolean(remote.is_active, true),
    nextRunAt: costDate(remote.next_run_at),
    lastRunAt: costDate(remote.last_run_at),
    lastStatus: lastStatus ? runStatus(lastStatus) : null,
    lastError: costText(remote.last_error),
    createdAt: costDate(remote.created_at),
    updatedAt: costDate(remote.updated_at),
  };
}

function mapScheduledReportRun(remote: RemoteScheduledReportRun): ScheduledReportRun {
  return {
    id: remote.id,
    scheduleId: costText(remote.schedule_id),
    scheduledFor: costDate(remote.scheduled_for),
    startedAt: costDate(remote.started_at),
    finishedAt: costDate(remote.finished_at),
    status: runStatus(remote.status),
    attempt: costNullableNumber(remote.attempt ?? null) ?? 1,
    rowCount: costNullableNumber(remote.row_count ?? null),
    recipients: parseList(remote.recipients),
    errorCode: costText(remote.error_code),
    errorMessage: costText(remote.error_message),
    trigger: remote.trigger === "manual" ? "manual" : "schedule",
  };
}

/**
 * Upstream timestamps here (`next_run_at`, `last_run_at`, run times) are UTC `YYYY-MM-DD HH:MM:SS`
 * with no zone marker; `parseISO` would read them as local time and shift every one by the
 * viewer's offset. `null` for an empty or unparseable value.
 */
export function parseUpstreamUtc(value: string): Date | null {
  if (!value) return null;
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(value) ? `${value.replace(" ", "T")}Z` : value;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms) : null;
}

export interface ScheduledReportInput {
  name: string;
  reportType: ScheduledReport["reportType"];
  groupId: string;
  vehicleId: string;
  frequency: ScheduledReportFrequency;
  weekdays: number[];
  monthDay: number | null;
  sendTime: string;
  timeZone: string;
  language: ScheduledReport["language"];
  formats: ScheduledReportFormat[];
  recipients: string[];
  isActive: boolean;
}

/** The fields upstream recomputes `next_run_at` from; sent together whenever any of them changes. */
export const TIMING_FIELDS = [
  "frequency",
  "weekdays",
  "monthDay",
  "sendTime",
  "timeZone",
] as const satisfies readonly (keyof ScheduledReportInput)[];

/**
 * Only keys present on `input` are sent, so a PUT carries just what changed. The timing block is
 * sent whole whenever `frequency` is present — callers changing any timing field must pass all of
 * `TIMING_FIELDS` — with the fields the frequency does not use cleared (`[]` / `null`), so a
 * monthly rule switched to daily does not keep a stale `month_day`.
 */
function toRemoteScheduledReport(input: Partial<ScheduledReportInput>) {
  const frequency = input.frequency;
  return {
    ...(input.name !== undefined ? { name: input.name } : {}),
    ...(input.reportType !== undefined ? { report_type: input.reportType } : {}),
    ...(input.groupId !== undefined ? { group_id: input.groupId === ALL ? null : input.groupId } : {}),
    ...(input.vehicleId !== undefined
      ? { vehicle_id: input.vehicleId === ALL ? null : input.vehicleId }
      : {}),
    ...(frequency !== undefined
      ? {
          frequency,
          weekdays: frequency === "monthly" ? [] : (input.weekdays ?? []).map(String),
          month_day:
            frequency === "monthly" && input.monthDay ? String(input.monthDay) : null,
          send_time: input.sendTime,
          timezone: input.timeZone,
        }
      : {}),
    ...(input.language !== undefined ? { language: input.language } : {}),
    ...(input.formats !== undefined ? { formats: input.formats } : {}),
    ...(input.recipients !== undefined ? { recipients: input.recipients } : {}),
    ...(input.isActive !== undefined ? { is_active: toRemoteFlag(input.isActive) } : {}),
  };
}

export async function listScheduledReports(): Promise<ScheduledReport[]> {
  const response = await apiRequest<ListResponse<RemoteScheduledReport>>("/api/scheduled-reports", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map(mapScheduledReport);
}

export async function createScheduledReport(input: ScheduledReportInput): Promise<string> {
  const response = await apiRequest<{ success: true; id: string }>("/api/scheduled-reports", {
    method: "POST",
    body: JSON.stringify(toRemoteScheduledReport(input)),
  });
  logCrud("create", "scheduled_report", input.name, response.id);
  return response.id;
}

/** `label` is the schedule's name for the activity log, since a partial input may not carry it. */
export async function updateScheduledReport(
  id: string,
  input: Partial<ScheduledReportInput>,
  label: string,
): Promise<void> {
  await apiRequest<{ success: true }>(`/api/scheduled-reports/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(toRemoteScheduledReport(input)),
  });
  logCrud("update", "scheduled_report", label, id);
}

export async function deleteScheduledReport(id: string, label: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/scheduled-reports/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  logCrud("delete", "scheduled_report", label, id);
}

export async function listScheduledReportRuns(id: string): Promise<ScheduledReportRun[]> {
  const response = await apiRequest<ListResponse<RemoteScheduledReportRun>>(
    `/api/scheduled-reports/${encodeURIComponent(id)}/runs`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapScheduledReportRun);
}

/** Queues an immediate send of the current period upstream; the mail arrives once the worker runs. */
export async function sendScheduledReportNow(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/scheduled-reports/${encodeURIComponent(id)}/runs`, {
    method: "POST",
  });
}
